import React, { useState } from 'react';
import styles from './productTable.module.css';
import { Downarrow, Leaf, Pdf, Uparrow } from '../../../../Svg';
import { Link } from 'react-router-dom';
import { extractAttributes, stripHtmlTags } from '../../../utils/utils';
import { setSelectedProducts, updateProductQuantity } from '../../../redux/excessSlice';
import { useDispatch } from 'react-redux';


// Catalogue manufacturer, else the one saved with the excess stock ("n/a" means none was given)
const getManufacturer = (product) => {
    const fromCatalogue = product?.productData?.productOverview?.manufacturer?.name;
    if (fromCatalogue) return fromCatalogue;
    const fromStock = product?.manufacturer;
    return fromStock && String(fromStock).toLowerCase() !== 'n/a' ? fromStock : '';
};

// Our own stock of this part (only parts that are in our catalogue have it)
const getOwnStock = (product) => {
    const catalogue = product?.productData;
    if (!catalogue || catalogue.status === 'OUT OF STOCK') return 0;
    return Number(catalogue.inventory?.qtyAvailable) || 0;
};

// Product photo, or a plain chip placeholder when there is no photo
const ProductImage = ({ src }) => {
    const [failed, setFailed] = useState(false);

    if (!src || failed) {
        return (
            <div className={styles.noProductImg} aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                    <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" />
                </svg>
            </div>
        );
    }

    return <img src={src} style={{ width: 50, height: 50 }} alt="" onError={() => setFailed(true)} />;
};

const ExcessProductTable = ({ products, addtoCart, quantities, cartLoading, scheduleLoading, selectedProducts, hide }) => {

    const [expandedProducts, setExpandedProducts] = useState({});
    const [localQuantities, setLocalQuantities] = useState({});

    const dispatch = useDispatch();

    const toggleShowAll = (productId) => {
        setExpandedProducts((prev) => ({
            ...prev,
            [productId]: !prev[productId],
        }));
    };

    const allEnvironmentalKeys = Array.from(
        new Set(products?.flatMap(product => Object.keys(product?.productData?.environmental || {})))
    );

    const allAdditionalResources = Array.from(
        new Set(products?.flatMap(product => Object.keys(product?.productData?.additionalResources || {})))
    );

    const handleCheckboxChange = (product) => {
        const isSelected = selectedProducts?.some(p => p._id === product._id);

        if (isSelected) {
            dispatch(setSelectedProducts(
                selectedProducts.filter(item => item._id !== product._id)
            ));
        } else {
            const selectedProduct = {
                _id: product._id,
                productNumber: product.productNumber ? String(product.productNumber).trim() : '',
                quantity: localQuantities[product._id] || 1,
                manufacturer: getManufacturer(product),
                productData: product.productData
            };
            dispatch(setSelectedProducts([...selectedProducts, selectedProduct]));
        }
    };

    const allSelected = products?.length > 0 &&
        products.every(product => selectedProducts?.some(p => p._id === product._id));

    const handleSelectAll = () => {
        if (allSelected) {
            const filtered = selectedProducts.filter(
                item => !products.some(p => p._id === item._id)
            );
            dispatch(setSelectedProducts(filtered));
        } else {
            const newItems = products
                .filter(product => !selectedProducts?.some(p => p._id === product._id))
                .map(product => ({
                    _id: product._id,
                    productNumber: product.productNumber?.trim(),
                    quantity: localQuantities[product._id] || 1,
                    manufacturer: getManufacturer(product),
                    productData: product.productData
                }));
            dispatch(setSelectedProducts([...selectedProducts, ...newItems]));
        }
    };

    const handleQuantityChange = (productId, value) => {
        const quantity = Math.max(1, parseInt(value) || 1);

        setLocalQuantities(prev => ({
            ...prev,
            [productId]: quantity
        }));

        if (selectedProducts?.some(p => p._id === productId)) {
            const updatedSelected = selectedProducts.map(item =>
                item._id === productId ? { ...item, quantity } : item
            );
            dispatch(setSelectedProducts(updatedSelected));
        }
    };

    return (
        <>
            <div className={styles.productTableContainer}>
                <div className={styles.selectAllBar}>
                    <button
                        className={styles.selectAllBtn}
                        onClick={handleSelectAll}
                    >
                        {allSelected ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedProducts?.length > 0 && (
                        <span className={styles.selectedCount}>
                            {selectedProducts.length} item{selectedProducts.length !== 1 ? 's' : ''} selected
                        </span>
                    )}
                </div>
                <table className={styles.productTable}>
                    <thead className={styles.productTableHead}>
                        <tr>
                            <th className={styles.compare}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={allSelected}
                                    />
                                    <span>#</span>
                                </div>
                            </th>
                            <th className={styles.dataSheet}>Data sheet</th>
                            <th className={styles.partNumber}>
                                <div className={styles.headerWithFilter}>
                                    <span>Part </span>
                                    <div className={styles.sortIcons}>
                                        <Uparrow />
                                        <Downarrow />
                                    </div>
                                </div>
                            </th>
                            {hide !== "hide" &&
                                <th className={styles.requiredQuantity}>Match Type</th>
                            }
                            <th className={styles.manufacturer}>
                                <div className={styles.headerWithFilter}>
                                    <span>Manufacturer</span>
                                    <div className={styles.sortIcons}>
                                        <Uparrow />
                                        <Downarrow />
                                    </div>
                                </div>
                            </th>
                            <th className={styles.stock}>
                                <div className={styles.headerWithFilter}>
                                    <span>Supplier Stock</span>
                                    <div className={styles.sortIcons}>
                                        <Uparrow />
                                        <Downarrow />
                                    </div>
                                </div>
                            </th>
                            <th className={styles.unitPrice}>Unit Price</th>
                            <th className={styles.requiredQuantity}>Required Quantity</th>

                            {allEnvironmentalKeys?.map((key) => (
                                <th className={styles.packageType} key={key}>{key}</th>
                            ))}
                            {allAdditionalResources?.map((key) => (
                                <th className={styles.packageType} key={key}>{key}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {products?.map((product, index) => (
                            <tr key={index}>
                                <td className={`${styles.compare}  ${styles.tdBorder}`}>
                                    <input type="checkbox"
                                        checked={selectedProducts?.some((p) => p._id === product._id)}
                                        onChange={() => handleCheckboxChange(product)}
                                    />
                                </td>
                                <td className={`${styles.dataSheet}  ${styles.tdBorder}`}>
                                    <div className={styles.dataCol}>
                                        {product?.productData?.productOverview?.datasheetUrl?.[0] && (
                                            <a
                                                href={product.productData.productOverview.datasheetUrl[0]}
                                                target='_blank'
                                                rel="noopener noreferrer"
                                            >
                                                <Pdf />
                                            </a>
                                        )}
                                        <Leaf />
                                    </div>
                                </td>
                                <td className={`${styles.partNumber}  ${styles.tdBorder}`}>
                                    <div className={styles.dataRow}>
                                        <ProductImage src={product?.productData?.assets?.featuredImage?.[0]} />
                                        <div className={styles.dataCol}>
                                            {/* Stays on this page; the part number is not a link */}
                                            <div className={styles.dataColorChange}>
                                                {product?.productNumber || "N/A"}
                                            </div>
                                            <div>
                                                {stripHtmlTags(
                                                    product?.productData?.productOverview?.description?.[0] ||
                                                    product?.productData?.description ||
                                                    product?.productData?.productOverview?.shortDescription ||
                                                    product?.productData?.productOverview?.name
                                                ) || "No description available"}
                                            </div>
                                            {!product?.productData && (
                                                <span className={styles.notInCatalogue}>Not available with us</span>
                                            )}
                                            {getOwnStock(product) > 0 && (
                                                <Link
                                                    className={styles.ownStock}
                                                    to={`/productCode/${encodeURIComponent(product?.productData?.productOverview?.productNumber?.[0] || product?.productNumber || '')}`}
                                                >
                                                    Electrowiz stock: {getOwnStock(product)} available
                                                    <span className={styles.ownStockArrow}>View product →</span>
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                {hide !== "hide" &&
                                    <td className={`${styles.manufacturer}  ${styles.tdBorder}`}>
                                        {product?.matchType}
                                    </td>
                                }
                                <td className={`${styles.manufacturer}  ${styles.tdBorder}`}>
                                    {getManufacturer(product) ?
                                        <div className={styles.dataColorChange}>{getManufacturer(product)}</div>
                                        :
                                        <div style={{ color: '#94a3b8' }}>-</div>
                                    }
                                </td>
                                <td className={`${styles.stock}  ${styles.tdBorder}`} style={{ textAlign: 'center' }}>
                                    <div style={{ color: '#222222', fontWeight: 700 }}>
                                        {product?.stock}
                                    </div>
                                    <div className={styles.dataMargin}>{product?.stock > 0 ? "in stock" : "out of stock"}</div>
                                </td>
                                <td className={`${styles.unitPrice} ${styles.tdBorder}`}>
                                    {product?.productData?.quantityTable
                                        ?.slice(
                                            0,
                                            expandedProducts[product?._id]
                                                ? product?.productData?.quantityTable?.length
                                                : 2
                                        )
                                        .map((i, idx) => (
                                            <div key={idx} className={styles.dataMargin}>
                                                {i?.breakQty}: <span style={{ color: '#222222', fontWeight: 700 }}>₹{i?.unitPrice}</span>
                                                <div>{i?.packaging}</div>
                                            </div>
                                        ))}
                                    {product?.productData?.quantityTable?.length > 2 && (
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                toggleShowAll(product?._id);
                                            }}
                                        >
                                            <div className={styles.dataColorChange}>
                                                {expandedProducts[product?._id] ? "Show Less" : "More Inventory"}
                                            </div>
                                        </a>
                                    )}
                                </td>
                                <td className={`${styles.requiredQuantity}  ${styles.tdBorder}`}>
                                    <input
                                        type="number"
                                        min="1"
                                        placeholder="Enter qty"
                                        className={styles.dataInput}
                                        value={localQuantities[product._id] || product.quantity || ""}
                                        onChange={(e) => handleQuantityChange(product._id, e.target.value)}
                                    />
                                    <div className={styles.dataMargin}>Min 1</div>
                                </td>


                                {allEnvironmentalKeys?.map((key) => (
                                    <td key={key} className={`${styles.packageType}  ${styles.tdBorder}`}>
                                        {product?.productData?.environmental[key]?.join(", ") ?? "N/A"}
                                    </td>
                                ))}

                                {allAdditionalResources?.map((key) => (
                                    <td key={key} className={`${styles.packageType} ${styles.tdBorder}`}>
                                        {product?.productData?.additionalResources?.[key]?.join(", ") || "N/A"}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default ExcessProductTable;
